import React from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Tooltip,
  Select,
  Dropdown,
  Menu,
  message,
} from 'antd';
import {
  SyncOutlined,
  MoreOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
  DeleteOutlined,
  FileMarkdownOutlined,
  FileWordOutlined,
  FilePptOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import JsonFile from '../../../assets/customSVG/JsonFile';
import TextFile from '../../../assets/customSVG/TextFile';

import { deleteDataSource } from '../../../reduxToolkit/services/corpus.service';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { cleanFileName, convertBytesToHumanReadable } from '../helpers';

const { Text, Paragraph } = Typography;

// Mapping of loader types to their corresponding display icon.
const loaderIcons = {
  pdf: <FilePdfOutlined className="text-lg text-[#EF4444]" />,
  md: <FileMarkdownOutlined className="text-lg text-[#EF4444]" />,
  doc: <FileWordOutlined className="text-lg text-[#EF4444]" />,
  ppt: <FilePptOutlined className="text-lg text-[#EF4444]" />,
  default: <FileTextOutlined className="text-lg text-[#6366F1]" />,
  txt: <TextFile />,
  json: <JsonFile />,
};

const DataSourceTable = ({
  loading,
  data,
  onSync,
  syncingIds = [],
  onMoreAction,
  deletingIds = [],
  updatingIds = [],
  pagination: paginationProp = {},
  onTableChange,
}) => {
  // Deconstruct pagination props
  const {
    current = 1,
    pageSize = 10,
    total = 0,
    pageSizeOptions = [5, 10, 20, 50],
    onChange,
    onPageSizeChange,
  } = paginationProp;

  const dispatch = useDispatch();
  const { currentBrand } = useSelector((state) => state.app);

  // Handle page change
  const handlePageChange = (page) => {
    if (onChange) {
      onChange(page, pageSize);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (size) => {
    if (onPageSizeChange) {
      onPageSizeChange(size);
    } else if (onChange) {
      onChange(1, size);
    }
  };

  // Calculate pagination values
  const effectivePage = current;
  const effectivePageSize = pageSize;
  const effectiveTotal = total;
  const pageCount = Math.max(
    1,
    Math.ceil(effectiveTotal / effectivePageSize) || 1,
  );
  const startIndex =
    effectiveTotal > 0 ? (effectivePage - 1) * effectivePageSize + 1 : 0;
  const endIndex = Math.min(effectivePage * effectivePageSize, effectiveTotal);

  // Using server-side pagination

  const headerCellClass =
    'bg-gray-50 px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const bodyCellClass = 'px-6 py-4 align-middle';
  const renderHeaderTitle = (label) => (
    <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </span>
  );

  // Handle table sorting
  const handleTableChange = (pagination, filters, sorter) => {
    if (onTableChange) {
      onTableChange(pagination, filters, sorter);
    }
  };

  // Column configuration for the datasource table.
  const columns = [
    {
      title: renderHeaderTitle('name'),
      dataIndex: 'displayName',
      key: 'displayName',
      className: `${bodyCellClass} min-w-[100px]- max-w-[450px] w-[450px]`,
      onHeaderCell: () => ({ className: headerCellClass }),
      render: (value) => (
        <div className="w-full">
          <Text
            className="text-gray-600 truncate block w-full capitalize"
            style={{
              maxWidth: '100%',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={value || ''}
          >
            {value ? cleanFileName(value) : '—'}
          </Text>
        </div>
      ),
    },
    {
      title: renderHeaderTitle('Type'),
      dataIndex: 'fileDetails',
      key: 'fileDetails',
      className: `${bodyCellClass}`, // Fixed width
      onHeaderCell: () => ({ className: headerCellClass }),
      render: (value) => {
        const icon = loaderIcons[value?.icon] || loaderIcons.default;
        return (
          <div className="flex items-center gap-1.5">
            <div className="text-sm">{icon}</div>

            <Text className="text-gray-600 text-xs truncate">
              {value?.type
                ? value.type.split('_').join(' ').toLowerCase()
                : '—'}
            </Text>
          </div>
        );
      },
    },
    {
      title: renderHeaderTitle('size'),
      dataIndex: 'fileSize',
      key: 'fileSize',
      className: `${bodyCellClass}`,
      // sorter: (a, b) => (a.fileSize || 0) - (b.fileSize || 0),
      // sortDirections: ['ascend', 'descend'],
      onHeaderCell: () => ({ className: headerCellClass }),
      render: (value) => {
        return (
          <Text className="text-gray-600 capitalize text-xs">
            {(value && convertBytesToHumanReadable(Number(value))) || '—'}
          </Text>
        );
      },
    },
    {
      title: renderHeaderTitle('Status'),
      dataIndex: 'status',
      key: 'status',
      className: bodyCellClass,
      onHeaderCell: () => ({ className: headerCellClass }),
      render: (value) => (
        <Tag
          color={value?.color || 'default'}
          icon={value?.icon}
          className="rounded-full"
        >
          <span>{value?.label || '—'}</span>
        </Tag>
      ),
    },
    {
      title: renderHeaderTitle('Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      className: bodyCellClass,
      // sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      sorter: () => {},
      sortDirections: ['descend', 'ascend'],
      // defaultSortOrder: 'descend',
      onHeaderCell: () => ({ className: headerCellClass }),
      render: (value) => (
        <Text className="text-gray-500 text-xs">
          {value ? dayjs(value).format('MMM DD, YYYY HH:mm') : '—'}
        </Text>
      ),
    },
    {
      title: renderHeaderTitle('Actions'),
      key: 'actions',
      className: `${bodyCellClass} text-right`,
      onHeaderCell: () => ({ className: `${headerCellClass} text-right` }),
      align: 'center',
      render: (_, record) => {
        const isSyncing = syncingIds.includes(record.id);
        const isDeleting = deletingIds.includes(record.id);
        const isUpdating = updatingIds.includes(record.id);
        const menuItemStyle = {
          margin: '4px 0',
        };
        const menuItems = [
          {
            key: 'sync',
            label: <span className="px-4">Sync</span>,
            icon: <SyncOutlined spin={isSyncing} />,
            disabled: isDeleting,
            onClick: () => onSync?.(record.id),
            style: menuItemStyle,
            className: 'my-1',
          },
          {
            key: 'delete',
            label: <span className="px-4">Delete</span>,
            icon: <DeleteOutlined />,
            danger: true,
            // disabled: isSyncing || isUpdating,
            onClick: async () => {
              if (!currentBrand?.brand_id) {
                message.error(
                  'Unable to determine brand. Please select a workspace.',
                );
                return;
              }

              try {
                const resultAction = await dispatch(
                  deleteDataSource({
                    id: record?.raw?.uuid,
                    brandId: currentBrand?.brand_id,
                  }),
                );

                if (deleteDataSource.fulfilled.match(resultAction)) {
                  message.success('Data source deleted successfully');
                } else {
                  const errorMessage =
                    // resultAction.payload ||
                    // resultAction.error?.message ||
                    'Unable to delete data source.';
                  message.error(errorMessage);
                  console.error(errorMessage);
                }
              } catch (error) {
                message.error(
                  'An error occurred while deleting the data source',
                );
                console.error('Delete error:', error);
              }
            },
            style: menuItemStyle,
            className: 'my-1',
          },
        ];

        const dropdownStyle = {
          minWidth: '160px',
          padding: '4px 0',
        };

        return (
          <div className="flex justify-center">
            <Dropdown
              menu={{
                items: menuItems,
                style: dropdownStyle,
                className: 'p-0',
              }}
              trigger={['click']}
              placement="bottom"
              overlayClassName="min-w-[160px]"
            >
              <Button
                type="text"
                shape="circle"
                icon={<MoreOutlined />}
                loading={isDeleting || isUpdating || isSyncing}
                onClick={(e) => e.preventDefault()}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Table
        loading={loading}
        columns={columns}
        dataSource={Array.isArray(data) ? data : []}
        pagination={false}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        className="w-full overflow-hidden rounded-lg border border-gray-200"
        onChange={handleTableChange}
      />
      <div className="flex flex-col items-start justify-between gap-4 text-sm text-gray-600 lg:flex-row lg:items-center">
        <div className="font-medium text-gray-700">
          {effectiveTotal === 0
            ? 'Showing 0 datasources'
            : `Showing ${startIndex}-${endIndex} of ${effectiveTotal} datasources`}
        </div>
        <Space size={16} className="flex-wrap">
          <Space>
            <Button
              icon={<LeftOutlined />}
              type="text"
              disabled={effectivePage <= 1}
              onClick={() => handlePageChange(effectivePage - 1)}
            />
            <div className="rounded-lg border border-indigo-100 bg-white px-4 py-1 text-base font-semibold text-indigo-600">
              {effectivePage}
            </div>
            <Button
              icon={<RightOutlined />}
              type="text"
              disabled={effectivePage >= pageCount}
              onClick={() => handlePageChange(effectivePage + 1)}
            />
          </Space>
          <Select
            value={effectivePageSize}
            onChange={handlePageSizeChange}
            options={pageSizeOptions.map((size) => ({
              label: `${size} / page`,
              value: size,
            }))}
            className="w-32"
          />
        </Space>
      </div>
    </div>
  );
};

DataSourceTable.propTypes = {
  loading: PropTypes.bool,
  data: PropTypes.array,
  onSync: PropTypes.func,
  syncingIds: PropTypes.array,
  onMoreAction: PropTypes.func,
  deletingIds: PropTypes.array,
  updatingIds: PropTypes.array,
  pagination: PropTypes.object,
  onTableChange: PropTypes.func,
};

export default DataSourceTable;
